import * as React from 'react';
import { noteGuideActivity } from '../lib/first-run-guide';
import { cn } from '../lib/utils';
import { Input } from './input';

/** One selectable category-tag option for the (optional) facet affordance.
 * `id` is what flows back through `onToggleTag` and ultimately becomes the
 * search `tagFilter`; `name` is the human label. */
export interface SearchCategoryOption {
  id: string;
  name: string;
}

export interface SearchBarProps {
  /** Called with the debounced, trimmed query text as the user types. The page
   * owns the resulting search state; this component only debounces keystrokes. */
  onQueryChange: (query: string) => void;
  /** Seed text for the input (e.g. restoring state). Not a controlled value —
   * the field owns its keystrokes so typing stays responsive while debounced. */
  initialQuery?: string;
  placeholder?: string;
  /** Debounce window in ms before `onQueryChange` fires. */
  debounceMs?: number;
  /** When provided (and non-empty), renders a row of category-tag filter chips.
   * Omit for a plain text-only search bar. */
  categoryOptions?: SearchCategoryOption[];
  /** The currently-selected tag ids (owned by the page). */
  selectedTagIds?: string[];
  /** Toggle handler for a chip — the page updates its own `selectedTagIds`. */
  onToggleTag?: (tagId: string) => void;
  /** The page's search request is in flight: the box shows a spinner (owner,
   * 2026-10-22: a search over a big room takes a moment and nothing said so). */
  searching?: boolean;
  className?: string;
}

/**
 * PRD-0004 §4 "find a document", available on every surface. A presentational,
 * debounced search input (shadcn/ui styling via {@link Input} + `cn`) with an
 * optional category-tag filter affordance. State lives in the page: the bar
 * debounces text into `onQueryChange`, and renders `selectedTagIds` against
 * `categoryOptions`, calling `onToggleTag` — it never owns the search query or
 * the selected facets itself.
 */
export function SearchBar({
  onQueryChange,
  initialQuery = '',
  placeholder = 'Find a document…',
  debounceMs = 250,
  categoryOptions,
  selectedTagIds = [],
  onToggleTag,
  searching = false,
  className,
}: SearchBarProps) {
  const [text, setText] = React.useState(initialQuery);
  // The text last handed to the page: while the box holds something else, the
  // debounce is still running and a search is about to start — also "working".
  const [reported, setReported] = React.useState(initialQuery.trim());

  // Keep the latest callback in a ref so the debounce effect depends only on
  // the text + delay (not a new function identity every render).
  const onQueryChangeRef = React.useRef(onQueryChange);
  React.useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  }, [onQueryChange]);

  React.useEffect(() => {
    const handle = setTimeout(() => {
      onQueryChangeRef.current(text.trim());
      setReported(text.trim());
      // A first-run guide ticks "Search" once someone has searched.
      if (text.trim()) noteGuideActivity('search');
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [text, debounceMs]);

  const selected = new Set(selectedTagIds);
  const working = searching || text.trim() !== reported;
  const showChips = Boolean(categoryOptions && categoryOptions.length > 0 && onToggleTag);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        >
          {/* magnifying glass (inline SVG — no icon dep) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </span>
        <Input
          type="search"
          role="searchbox"
          aria-label={placeholder}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-busy={working}
          className={cn('pl-9', working && 'pr-14')}
        />
        {working ? (
          <span
            role="status"
            className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 text-muted-foreground"
          >
            <span className="sr-only">Searching…</span>
            <span
              aria-hidden="true"
              className="block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
            />
          </span>
        ) : null}
      </div>

      {showChips ? (
        <div role="group" aria-label="Filter by category" className="flex flex-wrap gap-1.5">
          {categoryOptions!.map((option) => {
            const active = selected.has(option.id);
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={active}
                onClick={() => onToggleTag!(option.id)}
                className={cn(
                  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {option.name}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
